import React, { useEffect, useRef, useState } from 'react';
import { Collapse } from 'antd';
import { Link } from 'react-router-dom';
import './OpenClawFAQ.css';

const FAQ_ITEMS = [
  {
    key: '1',
    label: 'What can CellCog handle?',
    children: (
      <>
        Research with citations, documents (PDF, DOCX, PPTX), images and
        designed graphics, video and audio, interactive dashboards,
        spreadsheets with formulas, and 3D models. The catalog grows
        regularly — your agent auto-discovers new capabilities without
        code changes.
      </>
    ),
  },
  {
    key: '2',
    label: 'How does billing work?',
    children: (
      <>
        Pay-per-execution. Credits draw from the CellCog account that owns
        the API key. No per-seat pricing, no minimum commitments —
        agents pay only for the work they actually run.{' '}
        <Link to="/pricing">See pricing →</Link>
      </>
    ),
  },
  {
    key: '3',
    label: 'Skill install vs plugin install — what\u2019s the difference?',
    children: (
      <>
        The <strong>ClawHub skill install</strong> is a lightweight bundle
        — your agent learns every CellCog capability via skills. The{' '}
        <strong>OpenClaw plugin install</strong> bundles the skills plus
        routing and setup logic for a richer first-class integration. Both
        use the same API key and the same capability surface. Use skills
        if you want CellCog as capabilities; use the plugin if you want
        CellCog as a native piece of your OpenClaw environment.
      </>
    ),
  },
  {
    key: '4',
    label: 'Can other agent frameworks use CellCog too?',
    children: (
      <>
        Yes. CellCog speaks HTTP — any framework, script, or custom agent
        that can authenticate with an API key can delegate. OpenClaw is
        our first-class integration, with dedicated pages for{' '}
        <Link to="/linear">Linear</Link>,{' '}
        <Link to="/delegate/cursor">Cursor</Link>, and{' '}
        <Link to="/delegate/claude-code">Claude Code</Link>.
        For everything else, see{' '}
        <Link to="/delegate/other">Other Agents</Link>.
      </>
    ),
  },
  {
    key: '5',
    label: 'How is this different from MCP?',
    children: (
      <>
        MCP is a transport protocol — a standardized way for agents to
        describe and invoke tools. CellCog is an execution layer that
        ships <em>skills</em> (not just tool definitions) with a unified
        response shape, automatic progress updates, notify-on-completion
        daemon patterns, and auto-discovered capabilities. If MCP is
        "how the conversation happens," CellCog is "what gets done."
      </>
    ),
  },
  {
    key: '6',
    label: 'What happens when a task takes a long time?',
    children: (
      <>
        CellCog emits progress updates every 4 minutes automatically —
        no extra setup from you. Pair with <code>notify_session_key</code>{' '}
        + a daemon listener, and your agent gets notified on completion
        without polling. Long tasks never block your agent; nothing feels
        stuck to the end user.
      </>
    ),
  },
];

const OpenClawFAQ = () => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!ref.current) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible(true);
            io.disconnect();
          }
        });
      },
      { threshold: 0.2 },
    );
    io.observe(ref.current);
    return () => io.disconnect();
  }, []);

  return (
    <section ref={ref} className={`openclaw-faq ${visible ? 'is-visible' : ''}`}>
      <div className="section section-narrow">
        <div className="section-eyebrow">Frequently asked</div>
        <h2 className="section-title display-font">
          Questions we hear most.
        </h2>

        <Collapse
          ghost
          items={FAQ_ITEMS}
          className="openclaw-faq__collapse"
          expandIconPosition="end"
        />
      </div>
    </section>
  );
};

export default OpenClawFAQ;
